/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import prepareForOptions from "~services/utils/prepareForOptions";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "~/db/db";
import { saveLocalStorage } from "~services/utils/SaveIndexBD";
import { GetIndexBD } from "~services/utils/GetIndexBD";
import useParametros from "~/fetchs/useParametros";

const safeSetLoading = (fn, value) => {
  if (typeof fn === "function") {
    fn(value);
  }
};

const useClientesOptionsV2 = ({ type, setLoading = () => {}, idCliente, clientesDisponiveis }) => {
  const [clientes, setClientes] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const { getBotoesCliente } = useParametros();

  const arrayOptionsCliente = ["codigo", "razaoSocial", "documento"];
  const arrayOptionsProspect = ["codigoProspect", "razaoSocial", "documento"];
  const arrayOptionsSimulador = ["codigoGeral", "razaoSocial", "documento"];

  const normalizeString = (str) =>
    str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f\s]/g, "")
      .replace(/[./-]/g, "");

  const searchProspect = (cliente) => {
    return [
      (cliente.nome ?? "").toLowerCase(),
      (cliente.segmento ?? "").toLowerCase(),
      (cliente.grupoCentralizador ?? "").toLowerCase(),
      (cliente.documento ?? "").toLowerCase(),
      (cliente.codigoProspect ?? "").toLowerCase(),
    ].some((str) => normalizeString(str).includes(normalizeString(searchTerm.toLowerCase())));
  };

  const searchRecebedor = (cliente) => {
    return [
      (cliente.nome ?? "").toLowerCase(),
      (cliente.segmento ?? "").toLowerCase(),
      (cliente.grupoCentralizador ?? "").toLowerCase(),
      (cliente.documento ?? "").toLowerCase(),
      (cliente.codigo ?? "").toLowerCase(),
      (cliente.codigoProspect ?? "").toLowerCase(),
    ].some((str) => normalizeString(str).includes(normalizeString(searchTerm.toLowerCase())));
  };

  useEffect(() => {
    const fetchBuscaCliente = async () => {
      try {
        safeSetLoading(setLoading, true);
        const result = [];

        let query = db.cliente.orderBy("razaoSocial");

        if (type === "options") query = query.filter((c) => c.recebedor === 1);
        else if (type === "optionsProspect" || type === "optionsSimulacao") {
          if (type == "optionsSimulacao")
            query = query.filter(
              (opt) =>
                (idCliente != null && opt.id == idCliente) ||
                (opt?.trilha?.nome && getBotoesCliente(opt.trilha.nome, "simulador de ensacados"))
            );
          query = query.filter((c) => c.recebedor === 1 || c.clienteProspect === 1);
        } else if (type === "optionsFunil") {
          query = query.filter((opt) =>
            opt?.codigo && opt?.trilha?.nome ? ["IMO", "INFRA"].includes(opt?.trilha?.nome) : false
          );
        } else query = query.filter((c) => c.clienteCarteira === 1);

        if (searchTerm) {
          if (type === "prospect" || type === "optionsProspect") query = query.filter(searchProspect);
          else query = query.filter(searchRecebedor);
        }

        setTotal(await query.count());

        if (
          ["options", "optionsProspect", "optionsSimulacao", "optionsConversaoProspect", "optionsFunil"].includes(type)
        ) {
          const allAvailableCustomers = clientesDisponiveis
            ? query.filter((c) => clientesDisponiveis.includes(c.id))
            : query;
          await allAvailableCustomers.each((a) => result.push(a));
        } else {
          await query.offset(0).each((a) => result.push(a));
        }

        setClientes(result);
      } finally {
        safeSetLoading(setLoading, false);
      }
    };

    fetchBuscaCliente();
  }, [searchTerm, clientesDisponiveis]);

  const clientesTotal = useLiveQuery(() => {
    let query = db.cliente;
    if (type === "optionsProspect" || type === "optionsSimulacao") {
      query = query
        .where("clienteProspect")
        .equals(1)
        .and((e) => e.ativo);
    } else if (type === "options") query = query.where("clienteProspect").equals(0);
    else query = query.where("clienteCarteira").equals(1);

    if (searchTerm) {
      if (type === "optionsProspect") query = query.and(searchProspect);
      else if (type === "options") query = query.and(searchRecebedor);
    }
    return query.count();
  }, [searchTerm]);

  const handleSearch = (inputText) => {
    setSearchTerm(inputText);
  };

  const codigoGeral = (codigoProspect, codigo) => {
    if (codigoProspect != "" && codigoProspect != undefined && codigoProspect != null) return codigoProspect;
    return codigo;
  };

  const options = useMemo(() => {
    let optionsData = clientes;

    if (type === "optionsSimulacao") {
      optionsData = optionsData?.map((opt) => {
        return {
          ...opt,
          codigoGeral: codigoGeral(opt?.codigo, opt?.codigoProspect),
        };
      });
    }

    if (type === "optionsProspect") {
      return prepareForOptions(optionsData || [], arrayOptionsProspect, "id");
    }
    if (type === "optionsSimulacao") {
      return prepareForOptions(optionsData || [], arrayOptionsSimulador, "id");
    }
    return prepareForOptions(optionsData || [], arrayOptionsCliente, "id");
  }, [clientes]);

  useEffect(() => {
    if (clientesTotal !== undefined) setTotal(clientesTotal);
  }, [clientesTotal]);

  useEffect(() => {
    updateIndexFilter();
  }, [searchTerm]);

  useEffect(() => {
    updateIndexFilter(true);
  }, []);

  const updateIndexFilter = async (initiate = false) => {
    const filter = await GetIndexBD("cadastro-credito-filtro")
      .then((e) => {
        let data = e;

        if (!data || typeof data != "object") {
          data = {};
        }

        if (data[type] == undefined) {
          data[type] = {};
        } else if (initiate && data[type].filter) {
          setSearchTerm(data[type].filter);
        }
        data[type].filter = searchTerm;

        return saveLocalStorage("cadastro-credito-filtro", data, true).catch(() => {});
      })
      .catch(() => {
        // Swallow error silently for test compatibility
      });

    return filter;
  };

  return {
    total,
    clientes,
    options,
    searchTerm,
    handleSearch,
  };
};

export default useClientesOptionsV2;
